/**
 * One row of the Workshop Types list (BON-391). The row body opens the type's
 * edit page; the write affordances sit outside that button so an action click
 * never doubles as a navigation.
 *
 * Delete is offered for every type, including types in use. The server answers a
 * referenced type with a 409 ("deactivate instead"), and surfacing that reply is
 * more honest than hiding the control — the usage count shown here can also go
 * stale between the list load and the click.
 */
import { ChevronRight } from 'lucide-react';

import type { WorkshopType } from '../../../api/workshop-types';

/** Integer cents → "$65" / "$64.50", or an em dash when the default is unset. */
function fmtPriceCents(cents: number | null): string {
  if (cents === null) return '—';
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

const PILL =
  'text-xs px-2 py-0.5 rounded-md bg-stone-100 dark:bg-stone-700/60 text-stone-600 dark:text-stone-300';

interface WorkshopTypeRowProps {
  type: WorkshopType;
  /** Whether to render the write affordances at all. */
  canEdit: boolean;
  /** Disables the affordances while any write on the list is in flight. */
  busy: boolean;
  onOpen: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

export default function WorkshopTypeRow({
  type,
  canEdit,
  busy,
  onOpen,
  onToggleActive,
  onDelete,
}: WorkshopTypeRowProps) {
  const label = type.displayName ?? type.name;
  return (
    <li className="flex items-center gap-3 py-2.5 -mx-2 px-2 rounded transition hover:bg-stone-50 dark:hover:bg-[#2a3530]">
      {/* The pills live inside the button for hit area, so name it explicitly —
          otherwise its accessible name is the whole row read aloud. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Edit ${label}`}
        className="flex flex-1 min-w-0 items-center gap-3 text-left"
      >
        <span className="flex-1 min-w-0 truncate text-sm text-bark-900 dark:text-stone-100">
          {label}
          {!type.isActive && (
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-700/60 text-stone-500 dark:text-stone-400">
              Inactive
            </span>
          )}
        </span>
        <span className={`${PILL} font-mono tabular-nums`}>
          {fmtPriceCents(type.defaultPriceCents)}
        </span>
        <span className={PILL}>
          {type.defaultCapacity !== null ? `${type.defaultCapacity} seats` : '— seats'}
        </span>
        {type.workshopCount > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-md bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-400">
            {type.workshopCount} in use
          </span>
        )}
        <ChevronRight size={14} className="text-stone-300 dark:text-stone-600" />
      </button>

      {canEdit && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onToggleActive}
            className="text-xs text-moss-600 hover:underline disabled:opacity-50"
          >
            {type.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            className="text-xs text-red-500 hover:underline disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      )}
    </li>
  );
}
